import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
    DndContext,
    closestCorners,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ApiService, { NotFoundError } from './services/ApiService';
import AdminKeyService from './services/AdminKeyService';
import UrlService from './services/UrlService';
import AuthorizationService from './services/AuthorizationService';
import DecryptionError from './services/DecryptionError';
import OptionDeleteModal from './components/OptionDeleteModal';
import CloneElectionModal from './components/CloneElectionModal';
import DeleteElectionModal from './components/DeleteElectionModal';
import { useShare } from './hooks/useShare';
import NotFoundScreen from './components/NotFoundScreen';
import LinkText from './components/LinkText';
import LanguageSwitcher from './components/LanguageSwitcher';
import { usePageMetadata } from './hooks/usePageMetadata';

// Sortable Option Item Component
function SortableOptionItem({
    option,
    index,
    onOptionChange,
    onRemoveOption,
    onKeyDown,
    inputRefs,
    isOptionNameDuplicate,
    isSaving,
    canModify,
    t
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: option.id || `temp-${index}`,
        disabled: !canModify || isSaving
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`option-item ${isDragging ? 'dragging' : ''}`}
            {...attributes}
        >
            {canModify && (
                <button
                    className="btn btn-outline-danger btn-sm option-remove"
                    onClick={() => onRemoveOption(index)}
                    disabled={isSaving}
                    title={t('election:actions.removeOption')}
                    style={{width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0'}}
                >
                    ×
                </button>
            )}
            <input
                ref={el => inputRefs.current[index] = el}
                type="text"
                className={`form-control option-input ${isOptionNameDuplicate(index) ? 'border-danger' : ''}`}
                placeholder={t('election:form.optionPlaceholder', { number: index + 1 })}
                value={option.name || ''}
                onChange={(e) => onOptionChange(index, e.target.value)}
                onKeyDown={(e) => onKeyDown(index, e)}
                disabled={isSaving || !canModify}
                readOnly={!canModify}
            />
            {canModify && (
                <div
                    {...listeners}
                    className="drag-handle"
                    style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        color: '#6c757d',
                        fontSize: '18px',
                        userSelect: 'none'
                    }}
                    title="Drag to reorder"
                >
                    ⠿
                </div>
            )}
            {isOptionNameDuplicate(index) && (
                <div className="text-danger" style={{fontSize: '0.875rem', width: '100%'}}>
                    {t('validation:options.duplicateName')}
                </div>
            )}
        </div>
    );
}

function RankedElectionDetail() {
    const { t, i18n } = useTranslation(['election', 'common', 'validation']);
    const [rankedElection, setRankedElection] = useState({});
    const [options, setOptions] = useState([]);
    const [originalOptions, setOriginalOptions] = useState([]);
    const [error, setError] = useState(null);
    const [isDecryptionError, setIsDecryptionError] = useState(false);
    const [isNotFoundError, setIsNotFoundError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editableTitle, setEditableTitle] = useState('');
    const [isSavingTitle, setIsSavingTitle] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editableDescription, setEditableDescription] = useState('');
    const [isSavingDescription, setIsSavingDescription] = useState(false);
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [shareUrls, setShareUrls] = useState({ normal: '', admin: '' });
    const [hasChanges, setHasChanges] = useState(false);
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);
    const inputRefs = useRef([]);
    const [allowNonAdminModifications, setAllowNonAdminModifications] = useState(false);
    const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
    const { share, copySuccess } = useShare();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [optionToDelete, setOptionToDelete] = useState(null);
    const [deleteVoteInfo, setDeleteVoteInfo] = useState(null);
    const [isCheckingVotes, setIsCheckingVotes] = useState(false);
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [isCloning, setIsCloning] = useState(false);
    const [showDeleteElectionModal, setShowDeleteElectionModal] = useState(false);
    const [isDeletingElection, setIsDeletingElection] = useState(false);

    // Page metadata
    const { title, description } = usePageMetadata(rankedElection);

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        // Extract admin keys from URL
        UrlService.extractAndStoreAdminKeys(location, id);
        // Extract encryption keys from URL
        UrlService.extractAndStoreEncryptionKeys(location, id);

        setIsLoading(true);
        ApiService.getElection(id)
            .then(data => {
                setRankedElection(data);
                const electionOptions = data.options || [];
                setOptions(electionOptions);
                setOriginalOptions(electionOptions);
                setIsLoading(false);
                setError(null);
                setIsDecryptionError(false);

                // Sync URL with correct encryption params from localStorage
                UrlService.syncEncryptionKeysWithUrl(id);

                // Generate share URLs
                const normalUrl = UrlService.generateElectionShareUrl(id, false, data.isEncrypted);
                const adminUrl = UrlService.generateElectionShareUrl(id, true, data.isEncrypted);
                setShareUrls({ normal: normalUrl, admin: adminUrl });
            })
            .catch(error => {
                console.error('Error fetching ranked election:' + id, error);
                if (error instanceof NotFoundError) {
                    setIsNotFoundError(true);
                } else if (error instanceof DecryptionError) {
                    setError(t('common:errors.decryptionFailed'));
                    setIsDecryptionError(true);
                } else {
                    setError(t('election:errors.loadFailed'));
                }
                setIsLoading(false);
            });
    }, [id, location]); // eslint-disable-line react-hooks/exhaustive-deps

    const hasOptionsChanged = useCallback(() => {
        // Compare current options with original options
        if (options.length !== originalOptions.length) {
            return true;
        }
        
        // Check for new options (no ID) - these are always changes
        for (const currentOption of options) {
            if (!currentOption.id && currentOption.name.trim()) {
                return true;
            }
        }
        
        // Create maps for efficient lookup by ID
        const currentOptionsMap = new Map();
        const originalOptionsMap = new Map();
        
        options.forEach(option => {
            if (option.id) {
                currentOptionsMap.set(option.id, option);
            }
        });
        
        originalOptions.forEach(option => {
            if (option.id) {
                originalOptionsMap.set(option.id, option);
            }
        });
        
        // Check if any existing option has changed name
        for (const [id, currentOption] of currentOptionsMap) {
            const originalOption = originalOptionsMap.get(id);
            if (originalOption && currentOption.name !== originalOption.name) {
                return true;
            }
        }
        
        // Check if any original option is missing (deleted)
        for (const [id] of originalOptionsMap) {
            if (!currentOptionsMap.has(id)) {
                return true;
            }
        }

        // Check if the order of options has changed
        // Compare only options that have IDs (existing options)
        const currentOrderedIds = options.filter(option => option.id).map(option => option.id);
        const originalOrderedIds = originalOptions.filter(option => option.id).map(option => option.id);

        if (currentOrderedIds.length === originalOrderedIds.length) {
            for (let i = 0; i < currentOrderedIds.length; i++) {
                if (currentOrderedIds[i] !== originalOrderedIds[i]) {
                    return true;
                }
            }
        }

        return false;
    }, [options, originalOptions]);

    // Update hasChanges whenever options change
    useEffect(() => {
        setHasChanges(hasOptionsChanged());
    }, [hasOptionsChanged]);

    // Focus management - focus input when focusedOptionIndex changes
    useEffect(() => {
        if (focusedOptionIndex !== null && inputRefs.current[focusedOptionIndex]) {
            inputRefs.current[focusedOptionIndex].focus();
            setFocusedOptionIndex(null); // Reset after focusing
        }
    }, [focusedOptionIndex]);

    // Ensure inputRefs array matches options array length
    useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, options.length);
    }, [options.length]);

    // Sync toggle state with election data
    useEffect(() => {
        if (rankedElection.adminOnlyOptionModifications !== undefined) {
            setAllowNonAdminModifications(!rankedElection.adminOnlyOptionModifications);
        }
    }, [rankedElection.adminOnlyOptionModifications]);

    const handleOptionChange = (index, value) => {
        const newOptions = options.map((option, i) => 
            i === index ? { ...option, name: value } : option
        );
        setOptions(newOptions);
        setSaveSuccess(false);
    };

    const handleRemoveOption = async (index) => {
        const option = options[index];
        setOptionToDelete({ index, option });
        
        // If option has an ID (existing option), check if it has votes
        if (option.id) {
            setIsCheckingVotes(true);
            try {
                const voteInfo = await ApiService.getOptionVoteInfo(id, option.id);
                setDeleteVoteInfo(voteInfo);
                
                // If user can't delete this option, show modal immediately
                // If user can delete, show confirmation modal
                setShowDeleteModal(true);
            } catch (error) {
                console.error('Error checking option votes:', error);
                // If we can't check votes, proceed with caution
                setDeleteVoteInfo({ hasVotes: false, voteCount: 0, canDelete: true, isAdmin: false });
                setShowDeleteModal(true);
            } finally {
                setIsCheckingVotes(false);
            }
        } else {
            // New option (no ID), can be deleted without checking
            const newOptions = options.filter((_, i) => i !== index);
            setOptions(newOptions);
            setSaveSuccess(false);
        }
    };

    const handleConfirmDelete = () => {
        if (optionToDelete) {
            const newOptions = options.filter((_, i) => i !== optionToDelete.index);
            setOptions(newOptions);
            setSaveSuccess(false);
        }
        setShowDeleteModal(false);
        setOptionToDelete(null);
        setDeleteVoteInfo(null);
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setOptionToDelete(null);
        setDeleteVoteInfo(null);
    };

    const handleCloneElection = () => {
        setShowCloneModal(true);
    };

    const handleCancelClone = () => {
        setShowCloneModal(false);
    };

    const handleConfirmClone = async (newTitle) => {
        setIsCloning(true);
        setError(null);

        try {
            const clonedElection = await ApiService.cloneElection(id, newTitle);
            setShowCloneModal(false);
            // Navigate to the new cloned election
            navigate(UrlService.buildEncryptedElectionUrl(`/ranked-elections/${clonedElection.id}`, clonedElection.id));
        } catch (error) {
            console.error('Error cloning election:', error);
            setError(t('election:errors.cloneFailed'));
        } finally {
            setIsCloning(false);
        }
    };

    const handleDeleteElection = () => {
        setShowDeleteElectionModal(true);
    };

    const handleCancelDeleteElection = () => {
        setShowDeleteElectionModal(false);
    };

    const handleConfirmDeleteElection = async () => {
        setIsDeletingElection(true);
        setError(null);

        try {
            await ApiService.deleteElection(id);
            setShowDeleteElectionModal(false);
            // Navigate to home page after successful deletion
            navigate('/');
        } catch (error) {
            console.error('Error deleting election:', error);
            setError(t('election:errors.deleteFailed'));
            setShowDeleteElectionModal(false);
        } finally {
            setIsDeletingElection(false);
        }
    };

    const handleRevertChanges = () => {
        setOptions([...originalOptions]);
        setSaveSuccess(false);
        setError(null);
    };

    const handleAddOption = () => {
        setOptions([...options, { name: '' }]);
        setSaveSuccess(false);
        // Set focus to the newly added option (last index)
        setFocusedOptionIndex(options.length);
    };

    const handleAddOptionsFromClipboard = async () => {
        try {
            const clipboardText = await navigator.clipboard.readText();
            if (!clipboardText) {
                return;
            }

            // Split by lines and filter out empty lines
            const lines = clipboardText
                .split(/\r?\n/)
                .map(line => line.trim())
                .filter(line => line.length > 0);

            if (lines.length === 0) {
                return;
            }

            // Get existing option names for duplicate checking
            const existingNames = getExistingOptionNames();

            // Remove duplicates within clipboard content and filter out existing options
            const seenInClipboard = new Set();
            const uniqueNewLines = lines.filter(line => {
                const normalizedLine = line.trim().toLowerCase();

                // Skip if already exists in current options
                if (existingNames.has(normalizedLine)) {
                    return false;
                }

                // Skip if already seen in clipboard
                if (seenInClipboard.has(normalizedLine)) {
                    return false;
                }

                seenInClipboard.add(normalizedLine);
                return true;
            });

            if (uniqueNewLines.length === 0) {
                return; // No new unique options to add
            }

            // Add each unique new line as a new option
            const newOptions = uniqueNewLines.map(line => ({ name: line }));
            setOptions([...options, ...newOptions]);
            setSaveSuccess(false);
        } catch (error) {
            console.error('Error reading clipboard:', error);
            setError(t('election:errors.clipboardReadFailed'));
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            // Only add new option if current input is not empty
            if (options[index].name.trim()) {
                handleAddOption();
            }
        }
    };

    const isLastOptionEmpty = () => {
        if (options.length === 0) return false; // Allow first option
        const lastOption = options[options.length - 1];
        return !lastOption.name || !lastOption.name.trim();
    };

    const handleDragStart = useCallback((event) => {
        // Optional: Add any drag start logic here
    }, []);

    const handleScrollToOptions = useCallback(() => {
        const optionsCard = document.querySelector('.options-management-card');
        if (optionsCard) {
            optionsCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setOptions(currentOptions => {
                const oldIndex = currentOptions.findIndex(option =>
                    (option.id || `temp-${currentOptions.indexOf(option)}`) === active.id
                );
                const newIndex = currentOptions.findIndex(option =>
                    (option.id || `temp-${currentOptions.indexOf(option)}`) === over.id
                );

                if (oldIndex !== -1 && newIndex !== -1) {
                    const newOptions = arrayMove(currentOptions, oldIndex, newIndex);
                    setSaveSuccess(false);
                    return newOptions;
                }
                return currentOptions;
            });
        }
    }, []);

    const getDuplicateOptionNames = () => {
        const nameCount = new Map();
        const duplicates = new Set();
        
        options.forEach(option => {
            if (option.name && option.name.trim()) {
                const trimmedName = option.name.trim().toLowerCase();
                const count = nameCount.get(trimmedName) || 0;
                nameCount.set(trimmedName, count + 1);
                
                if (count >= 1) {
                    duplicates.add(trimmedName);
                }
            }
        });
        
        return duplicates;
    };

    const isOptionNameDuplicate = (index) => {
        const currentOption = options[index];
        if (!currentOption.name || !currentOption.name.trim()) {
            return false;
        }
        
        const trimmedName = currentOption.name.trim().toLowerCase();
        const duplicateNames = getDuplicateOptionNames();
        return duplicateNames.has(trimmedName);
    };

    const hasDuplicateOptions = () => {
        return getDuplicateOptionNames().size > 0;
    };

    const getExistingOptionNames = () => {
        const existingNames = new Set();
        options.forEach(option => {
            if (option.name && option.name.trim()) {
                existingNames.add(option.name.trim().toLowerCase());
            }
        });
        return existingNames;
    };

    const handleToggleNonAdminModifications = async () => {
        setIsUpdatingSettings(true);
        setError(null);
        
        try {
            const newValue = !allowNonAdminModifications;
            const updatedElection = await ApiService.updateElectionSettings(id, {
                adminOnlyOptionModifications: !newValue
            });
            
            setRankedElection(updatedElection);
            setAllowNonAdminModifications(newValue);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            setError(err.message || t('election:errors.settingsUpdateFailed'));
        } finally {
            setIsUpdatingSettings(false);
        }
    };

    const handleCopyNormalLink = async () => {
        // Build formatted text with title and description
        const titleLine = rankedElection?.description
            ? `${rankedElection.title} - ${rankedElection.description}`
            : rankedElection?.title || t('election:title');

        const formattedText = `${t('election:shareText')}\n${titleLine}`;

        await share({
            url: shareUrls.normal,
            title: rankedElection?.title || t('election:title'),
            text: formattedText,
            successMessage: t('election:states.linkCopied'),
            errorMessage: t('election:states.linkCopyFailed')
        });
    };

    const handleCopyAdminLink = async () => {
        // Build formatted text with title and description
        const titleLine = rankedElection?.description
            ? `${rankedElection.title} - ${rankedElection.description}`
            : rankedElection?.title || t('election:title');

        const formattedText = `${t('election:shareAdminText')}\n${titleLine}`;

        await share({
            url: shareUrls.admin,
            title: rankedElection?.title || t('election:title'),
            text: formattedText,
            successMessage: t('election:states.adminLinkCopied'),
            errorMessage: t('election:states.linkCopyFailed')
        });
    };


    const handleSaveChanges = async () => {
        setIsSaving(true);
        setError(null);

        // Filter out empty options and trim whitespace
        const validOptions = options
            .filter(option => option.name && option.name.trim())
            .map(option => ({ ...option, name: option.name.trim() }));

        // Check for duplicates before saving
        if (hasDuplicateOptions()) {
            setError(t('validation:options.removeDuplicates'));
            setIsSaving(false);
            return;
        }

        try {
            // Add position field to each option to preserve order
            const optionsWithPosition = validOptions.map((option, index) => ({
                ...option,
                position: index
            }));

            // Send only the options that have changed using PATCH
            const updates = {
                options: optionsWithPosition
            };

            await ApiService.updateElection(id, updates);
            
            // Re-read the election data to get the latest state from the database
            const freshData = await ApiService.getElection(id);
            setRankedElection(freshData);
            setOptions(freshData.options);
            setOriginalOptions(freshData.options);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleTitleEdit = () => {
        setEditableTitle(rankedElection.title || '');
        setIsEditingTitle(true);
    };

    const handleTitleCancel = () => {
        setIsEditingTitle(false);
        setEditableTitle('');
        setError(null);
    };

    const handleTitleSave = async () => {
        const trimmedTitle = editableTitle.trim();
        if (!trimmedTitle) {
            setError(t('validation:election.titleRequired'));
            return;
        }

        setIsSavingTitle(true);
        setError(null);

        try {
            const updatedElection = await ApiService.updateElection(id, {
                title: trimmedTitle
            });

            setRankedElection(updatedElection);
            setIsEditingTitle(false);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            setError(err.message || t('election:errors.titleSaveFailed'));
        } finally {
            setIsSavingTitle(false);
        }
    };

    const handleTitleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleTitleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleTitleCancel();
        }
    };

    const handleDescriptionEdit = () => {
        setEditableDescription(rankedElection.description || '');
        setIsEditingDescription(true);
    };

    const handleDescriptionCancel = () => {
        setIsEditingDescription(false);
        setEditableDescription('');
        setError(null);
    };

    const handleDescriptionSave = async () => {
        const trimmedDescription = editableDescription.trim();
        
        setIsSavingDescription(true);
        setError(null);

        try {
            const updatedElection = await ApiService.updateElection(id, {
                description: trimmedDescription
            });
            
            setRankedElection(updatedElection);
            setIsEditingDescription(false);
            setEditableDescription('');
            setSaveSuccess(true);
            
            // Clear success message after 3 seconds
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Error updating description:', error);
            setError(error.message || t('election:errors.descriptionUpdateFailed'));
        } finally {
            setIsSavingDescription(false);
        }
    };

    const handleDescriptionKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            handleDescriptionSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleDescriptionCancel();
        }
    };

    if (isLoading) {
        return (
            <div className="election-detail-container">
                <div className="container-fluid h-100">
                    <div className="row h-100 justify-content-center align-items-center">
                        <div className="col-12 text-center">
                            <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">{t('common:states.loading')}</span>
                            </div>
                            <p className="text-light mt-3">{t('election:states.loadingDetails')}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show not found error screen if election was not found
    if (isNotFoundError) {
        return <NotFoundScreen type="election" />;
    }

    // Show decryption error screen if decryption failed
    if (isDecryptionError) {
        return (
            <div className="election-detail-container">
                <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                    <LanguageSwitcher />
                </div>

                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="election-detail-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">{t('common:errors.decryptionTitle')}</h4>
                                    <p>{t('common:errors.decryptionMessage')}</p>
                                    <hr />
                                    <p className="mb-0">{t('common:errors.decryptionAction')}</p>
                                </div>
                                <div className="election-detail-nav mt-3">
                                    <Link to="/" className="btn btn-outline-secondary">
                                        {t('common:navigation.backToHome')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Check if current language is RTL (Arabic)
    const isRTL = i18n.language === 'ar';

    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <div className={`election-detail-container ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                <LanguageSwitcher />
            </div>
            
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 col-xl-8">
                        {/* Navigation */}
                        <div className="election-detail-nav mb-4 pt-4">
                            <Link to="/" className="btn btn-outline-light btn-sm">
                                {t('common:navigation.backToHome')}
                            </Link>
                        </div>

                        {/* Election Info Card */}
                        <div className="election-detail-card mb-4">
                            <div className="election-detail-header">
                                <div className="title-section">
                                    {isEditingTitle ? (
                                        <div className="title-edit-container">
                                            <input
                                                type="text"
                                                className="form-control title-input"
                                                value={editableTitle}
                                                onChange={(e) => setEditableTitle(e.target.value)}
                                                onKeyDown={handleTitleKeyDown}
                                                placeholder={t('election:form.titlePlaceholder')}
                                                autoFocus
                                                maxLength={200}
                                                disabled={isSavingTitle}
                                            />
                                            <div className="title-edit-actions mt-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-success btn-sm me-2"
                                                    onClick={handleTitleSave}
                                                    disabled={isSavingTitle || !editableTitle.trim()}
                                                >
                                                    {isSavingTitle ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                            {t('common:states.saving')}
                                                        </>
                                                    ) : (
                                                        `✓ ${t('common:buttons.save')}`
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={handleTitleCancel}
                                                    disabled={isSavingTitle}
                                                >
                                                    {t('common:buttons.cancel')}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="title-view-container">
                                            <h1 className="election-detail-title d-inline">{rankedElection.title}</h1>
                                            {AuthorizationService.canEditElectionTitle(id, rankedElection) && (
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm title-edit-btn ms-2"
                                                    onClick={handleTitleEdit}
                                                    title={t('election:actions.editTitle')}
                                                >
                                                    ✏️
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {/* Description editing */}
                                <div className="description-container">
                                    {isEditingDescription ? (
                                        <div className="description-edit-container">
                                            <textarea
                                                className="form-control description-input"
                                                value={editableDescription}
                                                onChange={(e) => setEditableDescription(e.target.value)}
                                                onKeyDown={handleDescriptionKeyDown}
                                                placeholder={t('election:form.descriptionPlaceholder')}
                                                autoFocus
                                                rows={3}
                                                maxLength={1000}
                                                disabled={isSavingDescription}
                                            />
                                            <div className="description-edit-actions mt-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-success btn-sm me-2"
                                                    onClick={handleDescriptionSave}
                                                    disabled={isSavingDescription}
                                                >
                                                    {isSavingDescription ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                            {t('common:states.saving')}
                                                        </>
                                                    ) : (
                                                        `✓ ${t('common:buttons.save')}`
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={handleDescriptionCancel}
                                                    disabled={isSavingDescription}
                                                >
                                                    {t('common:buttons.cancel')}
                                                </button>
                                            </div>
                                            <small className="text-muted">{t('election:form.keyboardShortcuts')}</small>
                                        </div>
                                    ) : (
                                        <div className="description-view-container">
                                            {rankedElection.description ? (
                                                <p className="election-detail-description d-inline"><LinkText text={rankedElection.description} /></p>
                                            ) : (
                                                <p className="election-detail-description text-muted d-inline fst-italic">{t('election:noDescription')}</p>
                                            )}
                                            {AuthorizationService.canEditElectionTitle(id, rankedElection) && (
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm description-edit-btn ms-2"
                                                    onClick={handleDescriptionEdit}
                                                    title={rankedElection.description ? t('election:actions.editDescription') : t('election:actions.addDescription')}
                                                >
                                                    ✏️
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Share buttons */}
                                <div className="share-buttons mt-3">
                                    <button
                                        className="btn btn-outline-primary btn-sm me-2"
                                        onClick={handleCopyNormalLink}
                                        title={t('election:actions.shareElection')}
                                    >
                                        📋 {t('election:actions.shareElection')}
                                    </button>
                                    {AdminKeyService.hasElectionAdminKey(id) && (
                                        <button
                                            className="btn btn-outline-warning btn-sm"
                                            onClick={handleCopyAdminLink}
                                            title={t('election:actions.shareAdminLink')}
                                        >
                                            🔑 {t('election:actions.shareAdminLink')}
                                        </button>
                                    )}
                                </div>
                                
                                {/* Copy Success Feedback */}
                                {copySuccess && (
                                    <div className="alert alert-success mt-2 mb-0" role="alert">
                                        {copySuccess}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions Card */}
                        <div className="election-detail-card mb-4">
                            <div className="election-detail-section-header">
                                <h3>{t('election:sections.whatWouldYouLikeToDo')}</h3>
                            </div>

                            <div className="election-actions">
                                <div className="row g-3">
                                    {/* Manage Election Options Button - Only for users with edit permissions */}
                                    {AuthorizationService.canModifyElectionOptions(id, rankedElection) && (
                                        <div className="col-12 mb-2">
                                            <button
                                                onClick={handleScrollToOptions}
                                                className="btn btn-danger btn-lg w-100 election-action-btn"
                                            >
                                                {t('election:actions.manageElectionOptionsAction')}
                                            </button>
                                        </div>
                                    )}
                                    <div className="col-md-6">
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/create-vote`, id)}
                                            className="btn btn-primary btn-lg w-100 election-action-btn"
                                        >
                                            {t('election:actions.castYourVote')}
                                        </Link>
                                    </div>
                                    <div className="col-md-6">
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/result`, id)}
                                            className="btn btn-success btn-lg w-100 election-action-btn"
                                        >
                                            {t('election:actions.viewElectionResult')}
                                        </Link>
                                    </div>
                                    <div className="col-md-6">
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/votes`, id)}
                                            className="btn btn-outline-secondary btn-lg w-100 election-action-btn"
                                        >
                                            {t('election:actions.viewAllVotes')}
                                        </Link>
                                    </div>
                                    <div className="col-md-6">
                                        <button
                                            type="button"
                                            onClick={handleCloneElection}
                                            className="btn btn-outline-primary btn-lg w-100 election-action-btn"
                                            disabled={isCloning}
                                        >
                                            {t('election:actions.cloneElection')}
                                        </button>
                                    </div>
                                    {AuthorizationService.canDeleteElection(id, rankedElection) && (
                                        <div className="col-md-6">
                                            <button
                                                type="button"
                                                onClick={handleDeleteElection}
                                                className="btn btn-outline-danger btn-lg w-100 election-action-btn"
                                                disabled={isDeletingElection}
                                            >
                                                {t('election:actions.deleteElection')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Options Management Card */}
                        <div className="election-detail-card options-management-card mb-4">
                            <div className="election-detail-section-header">
                                <h3>
                                    {AuthorizationService.canModifyElectionOptions(id, rankedElection)
                                        ? t('election:sections.manageElectionOptions')
                                        : t('election:sections.previewElectionOptions')
                                    }
                                </h3>
                                <p className="text-muted mb-0">
                                    {AuthorizationService.canModifyElectionOptions(id, rankedElection) 
                                        ? t('election:sections.optionsDescriptionEdit')
                                        : t('election:sections.optionsDescriptionView')
                                    }
                                </p>
                            </div>
                            
                            {/* Admin Toggle for Non-Admin Modifications */}
                            {AuthorizationService.canEditElectionTitle(id, rankedElection) && (
                                <div className="mb-3">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="allowNonAdminModifications"
                                            checked={allowNonAdminModifications}
                                            onChange={handleToggleNonAdminModifications}
                                            disabled={isUpdatingSettings}
                                        />
                                        <label className="form-check-label" htmlFor="allowNonAdminModifications">
                                            <strong>{t('election:settings.allowNonAdminModifications')}</strong>
                                        </label>
                                        <div className="form-text">
                                            {t('election:settings.allowNonAdminModificationsHelp')}
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="options-list">
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCorners}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={options.map(option => option.id || `temp-${options.indexOf(option)}`)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {options.map((option, index) => (
                                            <SortableOptionItem
                                                key={option.id || `temp-${index}`}
                                                option={option}
                                                index={index}
                                                onOptionChange={handleOptionChange}
                                                onRemoveOption={handleRemoveOption}
                                                onKeyDown={handleKeyDown}
                                                inputRefs={inputRefs}
                                                isOptionNameDuplicate={isOptionNameDuplicate}
                                                isSaving={isSaving}
                                                canModify={AuthorizationService.canModifyElectionOptions(id, rankedElection)}
                                                t={t}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>

                                {options.length === 0 && (
                                    <div className="text-center py-4 text-muted">
                                        {AuthorizationService.canModifyElectionOptions(id, rankedElection)
                                            ? t('election:states.noOptionsEdit')
                                            : t('election:states.noOptionsView')
                                        }
                                    </div>
                                )}
                            </div>
                            
                            {AuthorizationService.canModifyElectionOptions(id, rankedElection) && (
                                <div className="options-actions">
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={handleAddOption}
                                        disabled={isSaving || isLastOptionEmpty()}
                                    >
                                        + {t('election:actions.addOption')}
                                    </button>

                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={handleAddOptionsFromClipboard}
                                        disabled={isSaving}
                                    >
                                        📋 {t('election:actions.addOptionsFromClipboard')}
                                    </button>

                                    <button
                                        className="btn btn-outline-warning"
                                        onClick={handleRevertChanges}
                                        disabled={isSaving || !hasChanges}
                                        title={t('election:actions.revertChangesHelp')}
                                    >
                                        {t('common:buttons.revertChanges')}
                                    </button>
                                    
                                    <button
                                        className="btn btn-success"
                                        onClick={handleSaveChanges}
                                        disabled={isSaving || !hasChanges || hasDuplicateOptions()}
                                    >
                                        {isSaving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                {t('common:states.saving')}
                                            </>
                                        ) : (
                                            t('common:buttons.saveChanges')
                                        )}
                                    </button>
                                </div>
                            )}
                            
                            {error && (
                                <div className="alert alert-danger mt-3" role="alert">
                                    {error}
                                </div>
                            )}
                            
                            {saveSuccess && (
                                <div className="alert alert-success mt-3" role="alert">
                                    {t('election:states.changesSaved')}
                                </div>
                            )}
                        </div>

                        {/* Secondary Actions Card - Cast Vote Button for Mobile Users */}
                        <div className="election-detail-card mb-4">
                            <div className="election-actions">
                                <Link
                                    to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/create-vote`, id)}
                                    className="btn btn-primary btn-lg w-100 election-action-btn"
                                >
                                    {t('election:actions.castYourVote')}
                                </Link>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            
            {/* Option Delete Modal */}
            <OptionDeleteModal
                show={showDeleteModal}
                onHide={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                optionName={optionToDelete?.option?.name || ''}
                voteInfo={deleteVoteInfo}
                loading={isCheckingVotes}
            />
            
            {/* Clone Election Modal */}
            <CloneElectionModal
                show={showCloneModal}
                onHide={handleCancelClone}
                onConfirm={handleConfirmClone}
                originalTitle={rankedElection.title || ''}
                loading={isCloning}
            />

            {/* Delete Election Modal */}
            <DeleteElectionModal
                show={showDeleteElectionModal}
                onHide={handleCancelDeleteElection}
                onConfirm={handleConfirmDeleteElection}
                electionTitle={rankedElection.title || ''}
                loading={isDeletingElection}
            />
            </div>
        </>
    );
}

export default RankedElectionDetail;
